import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { getCurrencySymbol } from '../lib/currency';
import { useAuth } from '../context/AuthContext';

import { Printer, Check, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { key: 'details',     label: 'Project Details' },
  { key: 'metrics',     label: 'Key Metrics' },
  { key: 'executive',   label: 'Executive Summary' },
  { key: 'integrations', label: 'Integrations In Scope' },
  { key: 'cost',        label: 'Cost & Effort Analysis' },
  { key: 'timeline',    label: 'Project Timeline & Gantt' },
  { key: 'resources',   label: 'Resource Allocation' },
  { key: 'clientRes',   label: 'Customer Resources' },
  { key: 'risk',        label: 'Risk & Feasibility' },
  { key: 'recommendations', label: 'Recommendations' },
  { key: 'appendix',    label: 'Component Appendix' },
];

export default function QuotePrintPage() {
  const { estimateId } = useParams();
  const [data, setData] = useState(null);
  const sym = getCurrencySymbol(data?.currency_code || data?.estimate?.currency_code);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(() => Object.fromEntries(SECTIONS.map(s => [s.key, true])));
  const { user } = useAuth();
  const isViewer = user?.role === 'user';
  const [showFx, setShowFx] = useState(true);
  const hasPrinted = useRef(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/export/quote-data/${estimateId}`, { withCredentials: true })
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [estimateId]);

  const triggerPrint = useCallback(() => {
    setTimeout(() => window.print(), 400);
  }, []);

  useEffect(() => {
    if (data && !hasPrinted.current) {
      hasPrinted.current = true;
      triggerPrint();
    }
  }, [data, triggerPrint]);

  const toggleAll = (on) => setVisible(Object.fromEntries(SECTIONS.map(s => [s.key, on])));

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen gap-3">
      <Loader2 className="w-6 h-6 animate-spin text-[#4ABFCF]" />
      <span className="text-slate-500">Preparing print view...</span>
    </div>
  );
  if (!data) return <div className="p-8 text-center text-slate-500">No data available.</div>;

  const { estimate: est, group_breakdown, role_breakdown, components, timeline, narrative, data_integrity: di, new_developments: ndItems, new_dev_overhead: ndOverhead, integrations: estIntegrations } = data;
  const totalEffort = est.total_effort || 0;
  const totalCost = est.total_cost || 0;
  const orgLogo = est.organization_logo;
  const orgName = est.organization_name;
  const orgColor = est.organization_primary_color || '#0F2040';
  const enabledIntegrations = (estIntegrations || []).filter(i => i.enabled);

  return (
    <>
      {/* Print controls — hidden when printing */}
      <div className="print-controls no-print" data-testid="print-controls">
        <div className="max-w-[900px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold text-[#0F2040]">Print Settings — Select Sections</h2>
            <div className="flex gap-2">
              <button onClick={() => toggleAll(true)} className="text-xs text-[#4ABFCF] hover:underline" data-testid="print-select-all">Select All</button>
              <span className="text-slate-300">|</span>
              <button onClick={() => toggleAll(false)} className="text-xs text-slate-400 hover:underline" data-testid="print-deselect-all">Deselect All</button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            {SECTIONS.map(s => (
              <button key={s.key} onClick={() => setVisible(v => ({ ...v, [s.key]: !v[s.key] }))}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all
                  ${visible[s.key] ? 'bg-[#0F2040] text-white border-[#0F2040]' : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'}`}
                data-testid={`print-toggle-${s.key}`}>
                {visible[s.key] && <Check className="w-3 h-3" />}
                {s.label}
              </button>
            ))}
          </div>
          {!isViewer && (
            <label className="flex items-center gap-2 text-xs text-slate-600 mb-3 cursor-pointer" data-testid="print-toggle-fx">
              <input type="checkbox" checked={showFx} onChange={e => setShowFx(e.target.checked)} className="accent-[#0F2040]" />
              Show FX conversion basis (internal — untick for client-facing copies)
            </label>
          )}
          <button onClick={triggerPrint} className="flex items-center gap-2 px-5 py-2 bg-[#0F2040] text-white rounded-lg text-sm font-medium hover:bg-[#1A3055] transition-colors" data-testid="print-trigger-btn">
            <Printer className="w-4 h-4" /> Print Quote
          </button>
        </div>
      </div>

      {/* Printable document */}
      <div className="print-document" data-testid="print-document">
        {/* Co-branded header */}
        <div className="print-header" style={{ borderBottomColor: orgColor }}>
          <div className="print-header-row">
            {orgLogo && <img src={orgLogo} alt={orgName} className="print-org-logo" />}
            <div className="print-header-text">
              <h1 className="print-title" style={{ color: orgColor }}>Quote</h1>
              <p className="print-subtitle">{est.estimate_name}{orgName ? ` — ${orgName}` : ''}</p>
              <p className="print-date">{new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
            <div className="print-seertech-logo">
              <span style={{ color: '#4ABFCF', fontWeight: 700 }}>Seertech</span> Solutions
            </div>
          </div>
        </div>

        {/* Project Details */}
        {visible.details && (
          <div className="print-section">
            <h2 className="print-section-heading" style={{ color: orgColor }}>Project Details</h2>
            <table className="print-info-table">
              <tbody>
                <tr>
                  <td className="print-info-label">Project</td><td>{est.estimate_name}</td>
                  <td className="print-info-label">Organization</td><td>{orgName || '—'}</td>
                </tr>
                <tr>
                  <td className="print-info-label">Type</td><td>{(est.implementation_type || '').replace(/_/g, ' ')}</td>
                  <td className="print-info-label">Hosting</td><td>{(est.hosting_model || '').replace(/_/g, ' ')}</td>
                </tr>
                <tr>
                  <td className="print-info-label">Complexity</td><td className="capitalize">{est.complexity}</td>
                  <td className="print-info-label">Audience</td><td>{est.target_audience ? `${Number(est.target_audience).toLocaleString()} users` : '—'}</td>
                </tr>
                <tr>
                  <td className="print-info-label">Pricing basis</td>
                  <td colSpan={3} data-testid="quote-pricing-basis">
                    {[
                      est.contracting_entity_id ? est.contracting_entity_id.toUpperCase() : null,
                      est.region || null,
                      est.currency_code || null,
                    ].filter(Boolean).join(' · ') || '—'}
                    {!isViewer && showFx && est.fx_rate_to_aud && (est.currency_code || 'AUD').toUpperCase() !== 'AUD' ? (
                      <span style={{ color: '#64748B' }}>
                        {' '}(1 {est.currency_code} = A${Number(est.fx_rate_to_aud).toFixed(4)}{est.fx_rate_date ? ` · snapshot ${est.fx_rate_date}` : ''})
                      </span>
                    ) : null}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Key Metrics */}
        {visible.metrics && (
          <div className="print-section">
            <h2 className="print-section-heading" style={{ color: orgColor }}>Key Metrics</h2>
            <div className="print-metrics-row">
              {[
                ['Total Effort', `${totalEffort.toLocaleString(undefined, { maximumFractionDigits: 0 })} hrs`],
                ['Total Cost', `${sym}${totalCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}`],
                ['Blended Rate', totalEffort > 0 ? `${sym}${Math.round(totalCost / totalEffort).toLocaleString()}/hr` : '—'],
                ['Components', est.component_count],
                ['Duration', timeline ? `${timeline.total_duration_weeks} weeks` : '—'],
                ['Peak FTE', timeline ? timeline.peak_fte.toFixed(1) : '—'],
              ].map(([label, value], i) => (
                <div key={label} className="print-metric-card">
                  <div className="print-metric-label">{label}</div>
                  <div className="print-metric-value" style={{ color: orgColor }}>{value}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Executive Summary */}
        {visible.executive && narrative?.executive_summary && (
          <div className="print-section">
            <h2 className="print-section-heading" style={{ color: orgColor }}>Executive Summary</h2>
            {narrative.executive_summary.split('\n\n').filter(Boolean).map((p, i) => (
              <p key={`exec-${i}`} className="print-body">{p}</p>
            ))}
          </div>
        )}

        {/* Integrations In Scope */}
        {visible.integrations && enabledIntegrations.length > 0 && (
          <div className="print-section">
            <h2 className="print-section-heading" style={{ color: orgColor }}>Integrations In Scope</h2>
            <p className="print-body" style={{ marginBottom: 8 }}>The following integrations are included in the implementation scope ({enabledIntegrations.length} total):</p>
            <table className="print-table">
              <thead>
                <tr>
                  <th>Integration</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {enabledIntegrations.map((intg, i) => (
                  <tr key={`intg-${intg.type || i}`}>
                    <td className="font-medium">{intg.label}</td>
                    <td>{intg.notes || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Cost & Effort Analysis */}
        {visible.cost && (
          <div className="print-section">
            <h2 className="print-section-heading" style={{ color: orgColor }}>Cost & Effort Analysis</h2>
            <div className="print-avoid-break">
              <p className="print-sub-heading">By Component Group</p>
              <table className="print-table">
                <thead><tr>
                  <th>Group</th><th>Components</th><th>Effort</th><th>Cost</th><th>% Total</th>
                </tr></thead>
                <tbody>
                  {group_breakdown.map((g, i) => (
                    <tr key={`group-${g.name}`}><td className="font-medium">{g.name}</td><td>{g.count}</td><td>{g.effort.toLocaleString()}h</td><td>{sym}{g.cost.toLocaleString()}</td><td>{g.pct}%</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="print-avoid-break">
              <p className="print-sub-heading" style={{ marginTop: '12px' }}>By Role</p>
              <table className="print-table">
                <thead><tr><th>Role</th><th>Hours</th><th>Rate</th><th>Cost</th><th>% Total</th></tr></thead>
                <tbody>
                  {role_breakdown.map((r, i) => (
                    <tr key={`role-${r.role_label}`}><td className="font-medium">{r.role_label}</td><td>{r.hours.toLocaleString()}h</td><td>{sym}{r.rate}/hr</td><td>{sym}{r.cost.toLocaleString()}</td><td>{r.pct}%</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Project Timeline */}
        {visible.timeline && timeline && (
          <div className="print-section">
            <h2 className="print-section-heading" style={{ color: orgColor }}>Project Timeline</h2>
            <div className="print-avoid-break">
              <p className="print-body" style={{ marginBottom: 8 }}>
                Duration: <strong>{timeline.total_duration_weeks} weeks</strong> &middot; Effort: <strong>{timeline.total_effort_hours.toLocaleString()}h</strong> &middot; Peak FTE: <strong>{timeline.peak_fte}</strong>
              </p>
              <table className="print-table">
                <thead><tr><th>Phase</th><th>Start</th><th>End</th><th>Duration</th><th>Effort</th></tr></thead>
                <tbody>
                  {timeline.phases.map((p, i) => (
                    <tr key={`phase-${p.name}`}><td className="font-medium">{p.name}</td><td>Wk {p.start_week}</td><td>Wk {p.end_week}</td><td>{p.duration_weeks} wk</td><td>{p.effort_hours.toLocaleString()}h</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Gantt chart */}
            <div className="print-avoid-break" style={{ marginTop: 16 }}>
              <p className="print-sub-heading">Gantt Chart</p>
              <div style={{ display: 'flex', marginBottom: 4 }}>
                <div style={{ width: 140, flexShrink: 0 }} />
                <div style={{ flex: 1, position: 'relative', height: 14, overflow: 'hidden' }}>
                  {(() => {
                    const totalWks = timeline.total_duration_weeks || 1;
                    const step = totalWks <= 15 ? 1 : totalWks <= 30 ? 2 : totalWks <= 60 ? 5 : 10;
                    const labels = [];
                    for (let w = 1; w <= totalWks; w += step) labels.push(w);
                    return labels.map((w) => (
                      <div key={`wk-${w}`} style={{ position: 'absolute', left: `${((w - 1) / totalWks) * 100}%`, transform: 'translateX(-50%)', textAlign: 'center', fontSize: 8, color: '#94a3b8' }}>W{w}</div>
                    ));
                  })()}
                </div>
              </div>
              {timeline.phases.map((p, i) => {
                const maxWk = timeline.total_duration_weeks || 1;
                const barColors = ['#4ABFCF', '#0F2040', '#6366F1', '#F59E0B', '#10B981', '#EF4444'];
                return (
                  <div key={`gbar-${i}`} style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                    <div style={{ width: 140, flexShrink: 0, fontSize: 9, fontWeight: 600, color: '#0F2040', paddingRight: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                    <div style={{ flex: 1, position: 'relative', height: 20, background: '#f8fafc', borderRadius: 3 }}>
                      <div style={{
                        position: 'absolute', height: 20, borderRadius: 3,
                        left: `${((p.start_week - 1) / maxWk) * 100}%`,
                        width: `${Math.max((p.duration_weeks / maxWk) * 100, 4)}%`,
                        backgroundColor: barColors[i % barColors.length],
                        display: 'flex', alignItems: 'center', paddingLeft: 4,
                      }}>
                        <span style={{ fontSize: 7, color: '#fff', fontWeight: 600 }}>{p.duration_weeks}wk</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Resource Allocation */}
        {visible.resources && timeline && (
          <div className="print-section">
            <h2 className="print-section-heading" style={{ color: orgColor }}>Resource Allocation</h2>
            <table className="print-table">
              <thead><tr><th>Role</th><th>Total Hours</th><th>Peak FTE</th><th>Peak Week</th></tr></thead>
              <tbody>
                {Object.entries(timeline.role_summary).sort((a, b) => b[1].total_hours - a[1].total_hours).map(([, d], i) => (
                  <tr key={`res-${d.role_label}`}><td className="font-medium">{d.role_label}</td><td>{d.total_hours.toLocaleString()}h</td><td>{d.peak_fte}</td><td>Week {d.peak_week}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Customer Resources */}
        {visible.clientRes && timeline?.client_resources?.length > 0 && (
          <div className="print-section">
            <h2 className="print-section-heading" style={{ color: orgColor }}>Customer Resource Allocation</h2>
            <table className="print-table">
              <thead><tr><th>Role</th><th>Active Weeks</th><th>Avg Commitment</th><th>Peak Week</th><th>Peak %</th></tr></thead>
              <tbody>
                {timeline.client_resources.map((cr, i) => (
                  <tr key={`cr-${cr.label}`}><td className="font-medium">{cr.label}</td><td>{cr.active_week_ranges || '—'}</td><td>{cr.avg_commitment}%</td><td>Week {cr.peak_week}</td><td>{cr.peak_pct}%</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Risk & Feasibility */}
        {visible.risk && narrative?.risk_assessment && (
          <div className="print-section">
            <h2 className="print-section-heading" style={{ color: orgColor }}>Risk & Feasibility Assessment</h2>
            <div className="print-avoid-break">
              {narrative.risk_assessment.split('\n\n').filter(Boolean).map((p, i) => (
                <p key={`p-${i}`} className="print-body">{p}</p>
              ))}
            </div>
            {est.modifiers && Object.keys(est.modifiers).length > 0 && (
              <div className="print-avoid-break">
                <p className="print-sub-heading" style={{ marginTop: 12 }}>Complexity & Risk Factors</p>
                <table className="print-table">
                  <thead><tr><th>Factor</th><th>Value</th><th>Impact</th></tr></thead>
                  <tbody>
                    {Object.entries(est.modifiers).filter(([, v]) => typeof v === 'number').sort().map(([k, v]) => (
                      <tr key={k}><td>{k.replace(/_/g, ' ')}</td><td>{v.toFixed(2)}</td><td>{v > 1 ? 'Increases effort' : v < 1 ? 'Reduces effort' : 'Neutral'}</td></tr>
                    ))}
                    {ndItems && ndItems.length > 0 && (
                      <tr><td>new development</td><td>{Math.round(ndItems.reduce((sum, d) => sum + d.final_effort, 0))}h</td><td>Adds DE effort, PM, Testing, Training & Documentation overhead</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Data Migration Risk Callout */}
        {di && di.modifier !== 1.0 && (
          <div className="print-section print-avoid-break" data-testid="print-data-integrity">
            <div style={{
              border: `2px solid ${di.is_risk ? '#e11d48' : '#16a34a'}`,
              borderRadius: 6,
              padding: '12px 16px',
              backgroundColor: di.is_risk ? '#fff1f2' : '#f0fdf4',
            }}>
              <p style={{ fontWeight: 700, fontSize: 11, color: di.is_risk ? '#9f1239' : '#166534', marginBottom: 6 }}>
                Data Migration Risk Assessment — {di.label} ({di.modifier}x)
              </p>
              <p style={{ fontSize: 9, color: di.is_risk ? '#be123c' : '#15803d', lineHeight: 1.5 }}>
                {di.summary}
              </p>
              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <span style={{ fontSize: 8, padding: '2px 8px', borderRadius: 4, backgroundColor: di.is_risk ? '#fecdd3' : '#bbf7d0', color: di.is_risk ? '#9f1239' : '#166534' }}>
                  Migration Effort: {di.dm_effort?.toLocaleString()}h ({di.dm_pct}%)
                </span>
                {di.pm_testing_uplift_pct !== 0 && (
                  <span style={{ fontSize: 8, padding: '2px 8px', borderRadius: 4, backgroundColor: di.is_risk ? '#fecdd3' : '#bbf7d0', color: di.is_risk ? '#9f1239' : '#166534' }}>
                    PM & Testing: {di.pm_testing_uplift_pct > 0 ? '+' : ''}{di.pm_testing_uplift_pct}% each
                  </span>
                )}
              </div>
            </div>
          </div>
        )}

        {/* New Development Callout */}
        {ndItems && ndItems.length > 0 && (
          <div className="print-section print-avoid-break" data-testid="print-new-dev">
            <div style={{ border: '2px solid #7c3aed', borderRadius: 6, padding: '12px 16px', backgroundColor: '#f5f3ff' }}>
              <p style={{ fontWeight: 700, fontSize: 11, color: '#5b21b6', marginBottom: 6 }}>
                New Development Work — {ndItems.length} Item{ndItems.length > 1 ? 's' : ''}
              </p>
              {ndItems.map((nd, i) => (
                <p key={`sug-${i}`} style={{ fontSize: 9, color: '#4c1d95', margin: '2px 0' }}>
                  {nd.name} ({nd.size}) — {nd.final_effort}h
                </p>
              ))}
              {ndOverhead && (
                <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                  <span style={{ fontSize: 8, padding: '2px 8px', borderRadius: 4, backgroundColor: '#ddd6fe', color: '#5b21b6' }}>PM: +{ndOverhead.pm_hours}h</span>
                  <span style={{ fontSize: 8, padding: '2px 8px', borderRadius: 4, backgroundColor: '#ddd6fe', color: '#5b21b6' }}>Testing: +{ndOverhead.testing_hours}h</span>
                  <span style={{ fontSize: 8, padding: '2px 8px', borderRadius: 4, backgroundColor: '#ddd6fe', color: '#5b21b6' }}>Training: +{ndOverhead.training_hours}h</span>
                  <span style={{ fontSize: 8, padding: '2px 8px', borderRadius: 4, backgroundColor: '#ddd6fe', color: '#5b21b6' }}>Documentation: +{ndOverhead.documentation_hours}h</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recommendations */}
        {visible.recommendations && narrative?.recommendations && (
          <div className="print-section">
            <h2 className="print-section-heading" style={{ color: orgColor }}>Recommendations & Next Steps</h2>
            {narrative.recommendations.split('\n\n').filter(Boolean).map((p, i) => (
              <p key={`p-${i}`} className="print-body">{p}</p>
            ))}
          </div>
        )}

        {/* Component Appendix */}
        {visible.appendix && (
          <div className="print-section">
            <h2 className="print-section-heading" style={{ color: orgColor }}>Appendix: Component Breakdown</h2>
            <table className="print-table print-table-compact">
              <thead><tr><th>Component</th><th>Group</th><th>Effort</th><th>Cost</th></tr></thead>
              <tbody>
                {(() => {
                  const rows = [];
                  let currentGroup = null, groupEffort = 0, groupCost = 0, totalE = 0, totalC = 0;
                  components.forEach((c, i) => {
                    if (currentGroup !== null && c.group !== currentGroup) {
                      rows.push(<tr key={`sub-${currentGroup}`} className="print-subtotal"><td colSpan={2}>Subtotal: {currentGroup}</td><td>{groupEffort.toLocaleString(undefined, { maximumFractionDigits: 1 })}h</td><td>{sym}{groupCost.toLocaleString()}</td></tr>);
                      groupEffort = 0; groupCost = 0;
                    }
                    currentGroup = c.group; groupEffort += c.effort; groupCost += c.cost; totalE += c.effort; totalC += c.cost;
                    rows.push(<tr key={`comp-${c.name}`}><td>{c.name}</td><td className="text-slate-500">{c.group}</td><td>{c.effort}h</td><td>{sym}{c.cost.toLocaleString()}</td></tr>);
                  });
                  if (currentGroup !== null) rows.push(<tr key={`sub-${currentGroup}`} className="print-subtotal"><td colSpan={2}>Subtotal: {currentGroup}</td><td>{groupEffort.toLocaleString(undefined, { maximumFractionDigits: 1 })}h</td><td>{sym}{groupCost.toLocaleString()}</td></tr>);
                  rows.push(<tr key="total" className="print-total"><td colSpan={2}>TOTAL</td><td>{totalE.toLocaleString(undefined, { maximumFractionDigits: 1 })}h</td><td>{sym}{totalC.toLocaleString()}</td></tr>);
                  return rows;
                })()}
              </tbody>
            </table>
          </div>
        )}

        {/* Footer */}
        <div className="print-footer">
          Generated by Seertech Blueprint Studio — Seertech Solutions &middot; {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} &middot; Confidential
        </div>
      </div>

      <style>{`
        /* ─── Screen styles (controls bar) ─── */
        .print-controls {
          position: sticky; top: 0; z-index: 50;
          background: #fff; border-bottom: 2px solid #e2e8f0;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
        }

        /* ─── Document styles ─── */
        .print-document {
          max-width: 900px; margin: 0 auto; padding: 32px 40px;
          background: #fff; font-family: 'Segoe UI', system-ui, -apple-system, sans-serif;
          color: #1e293b; font-size: 11px; line-height: 1.5;
        }
        .print-header { padding-bottom: 16px; margin-bottom: 20px; border-bottom: 3px solid #0F2040; }
        .print-header-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
        .print-org-logo { height: 40px; width: auto; object-fit: contain; }
        .print-header-text { flex: 1; }
        .print-title { font-size: 22px; font-weight: 800; margin: 0; letter-spacing: -0.5px; }
        .print-subtitle { font-size: 11px; color: #64748b; margin: 2px 0 0; }
        .print-date { font-size: 9px; color: #94a3b8; margin-top: 2px; }
        .print-seertech-logo { font-size: 11px; color: #64748b; white-space: nowrap; }
        .print-section { margin-bottom: 18px; }
        .print-section-heading { font-size: 14px; font-weight: 700; margin: 0 0 8px; padding-bottom: 4px; border-bottom: 2px solid currentColor; }
        .print-sub-heading { font-size: 10px; font-weight: 700; color: #64748b; text-transform: uppercase; letter-spacing: 0.5px; margin: 0 0 4px; }
        .print-body { font-size: 10.5px; color: #334155; margin: 0 0 6px; line-height: 1.6; orphans: 3; widows: 3; }
        .print-info-table { width: 100%; border-collapse: collapse; font-size: 10px; }
        .print-info-table td { padding: 3px 8px; border: 1px solid #e2e8f0; }
        .print-info-label { font-weight: 700; color: #0F2040; width: 90px; background: #f8fafc; }
        .print-metrics-row { display: flex; gap: 8px; }
        .print-metric-card { flex: 1; border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 12px; text-align: center; }
        .print-metric-label { font-size: 8px; text-transform: uppercase; color: #94a3b8; font-weight: 600; letter-spacing: 0.5px; }
        .print-metric-value { font-size: 16px; font-weight: 800; margin-top: 2px; }
        .print-table { width: 100%; border-collapse: collapse; font-size: 10px; }
        .print-table th { background: #0F2040; color: #fff; text-align: left; padding: 4px 8px; font-weight: 600; font-size: 9px; }
        .print-table td { padding: 3px 8px; border-bottom: 1px solid #e2e8f0; }
        .print-table tbody tr:nth-child(even) { background: #f8fafc; }
        .print-table-compact td { padding: 2px 6px; font-size: 9px; }
        .print-table-compact th { padding: 3px 6px; font-size: 8px; }
        .print-subtotal td { background: #eef0f2 !important; font-weight: 700; color: #0F2040; }
        .print-total td { background: #0F2040 !important; color: #fff !important; font-weight: 700; }
        .print-footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #e2e8f0; text-align: center; font-size: 8px; color: #94a3b8; }

        /* ─── Print media ─── */
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print-document { max-width: none; padding: 0; margin: 0; }

          /* Every section avoids internal page breaks */
          .print-section {
            page-break-inside: avoid;
            break-inside: avoid;
            page-break-after: auto;
            break-after: auto;
          }

          /* Sub-blocks within sections also avoid breaks */
          .print-avoid-break {
            page-break-inside: avoid;
            break-inside: avoid;
          }

          /* Section headings stay with their content */
          .print-section-heading {
            page-break-after: avoid;
            break-after: avoid;
          }
          .print-sub-heading {
            page-break-after: avoid;
            break-after: avoid;
          }

          /* Tables: rows don't break, header repeats */
          .print-table { page-break-inside: auto; break-inside: auto; }
          .print-table tr { page-break-inside: avoid; break-inside: avoid; }
          .print-table thead { display: table-header-group; }

          /* Metrics row stays together */
          .print-metrics-row {
            page-break-inside: avoid;
            break-inside: avoid;
          }

          /* Header stays on first page */
          .print-header {
            page-break-inside: avoid;
            break-inside: avoid;
          }

          /* Footer stays together */
          .print-footer {
            page-break-inside: avoid;
            break-inside: avoid;
          }

          /* Prevent orphaned headings at bottom of page */
          h2 { page-break-after: avoid; break-after: avoid; }
          p { orphans: 3; widows: 3; }
        }
      `}</style>
    </>
  );
}
